import { noop } from '@utils/noop';
import { Dispatch, createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ObjectSchema, object, string, number, mixed } from 'yup';
import * as yup from 'yup';

export type UpdateProviderConfigFormState = {
  maxWin: number;
  defaultBet: number;
  minBet: number;
  maxBet: number;
  betSuggestions: any;
  minAutoCashout: number;
  maxAutoCashout: number;
  defaultAutoCashout: number;
  isRoundable: string | boolean;
  currency: string;
};

export const updateProviderConfigFormStateDefault: UpdateProviderConfigFormState = {
  maxWin: 0,
  defaultBet: 0,
  minBet: 0,
  maxBet: 0,
  betSuggestions: [],
  minAutoCashout: 0,
  maxAutoCashout: 0,
  defaultAutoCashout: 0,
  isRoundable: '',
  currency: '',
};

export const updateProviderConfigSchemaDefault: ObjectSchema<
  Partial<UpdateProviderConfigFormState>
> = object<Partial<UpdateProviderConfigFormState>>().shape({
  maxWin: number().required('Max Win is required').positive('Max Win must be a positive number'),
  defaultBet: number()
    .required('Default Bet is required')
    .positive('Default Bet must be a positive number'),
  minBet: number()
    .required('Min Bet is required')
    .positive('Min Bet must be a positive number')
    .max(yup.ref('maxBet'), 'Min Bet must be less than Max Bet')
    .test(
      'min-bet-roundable',
      'Min Bet must be at least 1 when Is Roundable is true',
      function (value) {
        const { isRoundable } = this.parent;
        if (isRoundable === true || isRoundable === 'true') {
          return value !== undefined && value >= 1;
        }
        return true;
      }
    ),
  maxBet: number()
    .required('Max Bet is required')
    .positive('Max Bet must be a positive number')
    .min(yup.ref('minBet'), 'Max Bet must be greater than Min Bet'),
  betSuggestions: mixed().test(
    'is-valid',
    'Bet Suggestions must be a string, number, or an array of strings/numbers',
    (value) =>
      Array.isArray(value)
        ? value.every((v) => typeof v === 'string' || typeof v === 'number')
        : typeof value === 'string' || typeof value === 'number'
  ),
  minAutoCashout: number()
    .required('Min Auto Cashout is required')
    .positive('Min Auto Cashout must be a positive number')
    .max(yup.ref('maxAutoCashout'), 'Min Auto Cashout must be less than Max Auto Cashout'),
  maxAutoCashout: number()
    .required('Max Auto Cashout is required')
    .positive('Max Auto Cashout must be a positive number')
    .min(yup.ref('minAutoCashout'), 'Max Auto Cashout must be greater than Min Auto Cashout'),
  defaultAutoCashout: number()
    .required('Default Auto Cashout is required')
    .positive('Default Auto Cashout must be a positive number')
    .min(
      yup.ref('minAutoCashout'),
      'Default Auto Cashout must be greater than or equal to Min Auto Cashout'
    )
    .max(
      yup.ref('maxAutoCashout'),
      'Default Auto Cashout must be less than or equal to Max Auto Cashout'
    ),
  isRoundable: string().required('Boolean is required'),
  currency: string()
    .required('Currency is required')
    .min(3, 'Currency must be at least 3 characters long'),
});

type UpdateProviderConfigFormContextType = {
  schema: ObjectSchema<Partial<UpdateProviderConfigFormState>>;
  setSchema: Dispatch<ObjectSchema<Partial<UpdateProviderConfigFormState>>>;
  methods?: UseFormReturn<UpdateProviderConfigFormState>;
};

const updateProviderConfigFormContextDefault = {
  schema: updateProviderConfigSchemaDefault,
  setSchema: noop,
} satisfies UpdateProviderConfigFormContextType;

export const UpdateProviderConfigFormContext = createContext<UpdateProviderConfigFormContextType>(
  updateProviderConfigFormContextDefault
);

export const useUpdateProviderConfigForm = () => {
  const { methods } = useContext(UpdateProviderConfigFormContext);
  if (!methods) {
    throw new Error('UpdateProviderConfigFormContext is empty');
  }

  return methods;
};

export const useManageUpdateProviderConfigSchema = () => {
  const { setSchema } = useContext(UpdateProviderConfigFormContext);

  return { setSchema };
};
